import { Grid } from '@mui/material';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import React, { useEffect } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList, FixedSizeList as List } from 'react-window';
import styled from 'styled-components';

import { Driver } from '../../../../../domain/driver/Driver';
import { useSetActivePlanFocusedStop } from '../../../../../hooks/plan/useSetActivePlanFocusedStop';
import Text from '../../../../../react-components/Text';
import { StopChip } from '../../plan-stop-chip/StopChip';
import { VirtualizedRowContainer, VirtualizedStopRow } from '../../plan-stop-chip/VirtualizedStopRow';

interface DriverStopsEmptyComponentProps {
  isDraggingOver: boolean;
}

const DriverStopsEmptyComponent = styled(Grid)<DriverStopsEmptyComponentProps>`
  height: 54px;
  background-color: transparent;
  ${(props: DriverStopsEmptyComponentProps) => {
    if (props.isDraggingOver) {
      return `
        border: 2px dashed ${`${RoutalPalette.primary20}bd`};
      `;
    }
    return `
      border: 1px solid ${RoutalPalette.neutral20};
    `;
  }}
`;

const DriverStopsEmpty = ({ isDraggingOver }: { isDraggingOver: boolean }) => {
  return (
    <Grid
      container
      style={{ backgroundColor: RoutalPalette.neutral05, padding: `0 8px`, height: `100%` }}
      alignItems="center"
      justifyContent="center"
    >
      <DriverStopsEmptyComponent
        container
        justifyContent="center"
        alignContent="center"
        isDraggingOver={isDraggingOver}
      >
        <Text
          variant="label"
          intlId="planoverview.route.no_assigned_stops"
          style={{ color: isDraggingOver ? RoutalPalette.primary40 : RoutalPalette.neutral20 }}
        />
      </DriverStopsEmptyComponent>
    </Grid>
  );
};

interface DriverStopsListProps {
  driver: Driver;
  expanded: boolean;
}

export const DriverStopsList = ({ driver, expanded }: DriverStopsListProps) => {
  const listRef = React.createRef<FixedSizeList>();
  const { focusedStop } = useSetActivePlanFocusedStop();

  useEffect(() => {
    if (focusedStop && expanded && listRef.current) {
      const driverStopsId = driver.stops.map((stop) => stop.id);
      if (driverStopsId.includes(focusedStop.id)) {
        const index = driverStopsId.indexOf(focusedStop.id);
        listRef.current.scrollToItem(index, `center`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedStop]);

  const isScrollable = () => {
    if (listRef.current) {
      const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
      const thirtyPercentOfViewportHeight = viewportHeight * 0.3;
      return (listRef.current.props.itemCount + 1) * listRef.current.props.itemSize > thirtyPercentOfViewportHeight;
    }
    return false;
  };

  return (
    <div
      style={{ backgroundColor: RoutalPalette.neutral05, height: `100%`, ...(!expanded ? { display: `none` } : {}) }}
    >
      <Droppable
        droppableId={driver.id}
        mode="virtual"
        renderClone={(provided, snapshot, rubric) => (
          <VirtualizedRowContainer {...provided.draggableProps} {...provided.dragHandleProps}>
            <StopChip stop={driver.stops[rubric.source.index]} isDragging={snapshot.isDragging} />
          </VirtualizedRowContainer>
        )}
      >
        {(provided, snapshot) => {
          const isDraggingOver = snapshot.isDraggingOver && !snapshot.draggingFromThisWith;

          return (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                position: `relative`,
                height: `max(min(${64 * (driver.stops.length + (isDraggingOver ? 1 : 0))}px, 30vh), 64px)`,
                padding: `4px`,
                paddingBottom: isDraggingOver && isScrollable() ? `64px` : `4px`,
                transition: `all 0.2s ease-in-out`,
              }}
            >
              {driver.stops.length === 0 ? (
                <div
                  style={{
                    position: `absolute`,
                    height: 64,
                    width: `calc(100% - 8px)`,
                  }}
                >
                  <DriverStopsEmpty isDraggingOver={snapshot.isDraggingOver} />
                </div>
              ) : null}

              <AutoSizer>
                {({ height, width }: any) => {
                  return (
                    <List
                      ref={listRef}
                      height={height}
                      width={width}
                      itemCount={driver.stops.length}
                      itemSize={64}
                      outerRef={provided.innerRef}
                      itemData={driver.stops}
                      style={{
                        paddingBottom: isDraggingOver ? `64px` : `0px`,
                        transition: `all 0.2s ease-in-out`,
                      }}
                    >
                      {VirtualizedStopRow}
                    </List>
                  );
                }}
              </AutoSizer>
            </div>
          );
        }}
      </Droppable>
    </div>
  );
};
